import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// The note draft (E09-S4-T6), ported from noteCard() in mockups/kit/components/cards.js: the note the
// assistant drafted for the user to paste, read-only, with its length against the field's limit and a
// Copy control. Registered with the card host for the note-draft kind (D-06).
//
// Every word of the note is the card's: the text is shown exactly as the card carries it, and Copy
// puts exactly that string on the clipboard. The surface adds the catalogue's labels and nothing else,
// and never writes into the application: the user pastes.
//
//   - Blanks come from the card's own marking (card.blanks, offsets into the text), never from
//     reading brackets in the text. Each is drawn as a marked gap labelled 'yours to fill in' with the
//     card's hint, and a line under the text says the note still has one.
//   - Length is counted the way the application's field counts it — UTF-16 code units, as a textarea
//     maxlength does — so the counter's 255 is the field's 255. A grapheme count would disagree at
//     the limit (an emoji is two units there).
//   - Copy is withheld, with the reason said beside it, when the text is longer than the field takes
//     or holds an identifier token (a token-shaped run TokenSchema accepts): a token pasted into the
//     firm's audit trail means nothing to anyone else. A blank does not withhold it: the user fills
//     it in where they paste.
//   - The clipboard writer is injected (writeClipboard); the default is the browser's. A copy that
//     fails says so, on the card and assertively through the Announcer — never a silent no-op the
//     user would only discover by pasting. Copied is said politely. No live region here.
//
// Where this differs from the kit:
//   - The kit strips the brackets from a blank's text inside the mark. Here the mark holds the card's
//     own characters, so what is shown is what Copy writes.
//   - The kit names the window the note is for, flags an audit-trail claim and says what the draft
//     was built from. The card carries none of that, so none of it is shown.
//   - The withheld-copy reasons and the copy-failed line are new: the kit's Copy always works.
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { NOTE_MAX_LENGTH, TOKEN_RE, TokenSchema, } from "@waypoint/contracts";
import { useAnnounceWhen } from "../../a11y/announcer.js";
import { t } from "../../i18n/t.js";
import { Icon } from "../../icons/index.js";
import { Button } from "../../ui/button.js";
import { cn } from "../../ui/cn.js";
import "../cards/cards.css";
import "./note-draft.css";
/** The browser's clipboard. Rejects where there is none (an insecure page, an old browser). */
export const browserClipboard = (text) => {
    // Typed as possibly absent: the DOM types promise a clipboard that an insecure page does not have.
    const holder = globalThis;
    const clipboard = holder.navigator?.clipboard;
    if (clipboard === undefined)
        return Promise.reject(new Error("no clipboard"));
    return clipboard.writeText(text);
};
/** How long 'Copied' shows before the control reads 'Copy' again, as the kit has it. */
export const COPIED_MS = 2_200;
/** The counter turns to the caution colour this close to the limit, as the kit's does. */
const NEAR = 25;
/** The draft's length as the application's field counts it: UTF-16 code units. */
export function noteLength(text) {
    return text.length;
}
/** The field's limit: the card's maxLength, never more than the contract's NOTE_MAX_LENGTH. */
export function noteLimit(card) {
    const max = card.maxLength;
    return typeof max === "number" && Number.isInteger(max) && max > 0 && max <= NOTE_MAX_LENGTH
        ? max
        : NOTE_MAX_LENGTH;
}
// The token check finds every token-shaped run in the text, wherever it sits — at a sentence's edge,
// before a hyphen ("ACCT-…-related"), glued to letters ("xACCT-…") — and asks TokenSchema of each.
// The grammar is the contracts' (D-03) and is not restated: the finder is TOKEN_RE with its anchors
// taken off, as packages/speech-text does. A run glued to more text still counts: the check errs
// towards withholding Copy, since what leaks is a token in the firm's audit trail.
const FINDER = new RegExp(TOKEN_RE.source.replace(/^\^/, "").replace(/\$$/, ""), "g");
/** The token-shaped runs of `text` that TokenSchema accepts, each once, in order. */
export function tokensIn(text) {
    const found = [];
    for (const [run] of text.matchAll(FINDER))
        if (TokenSchema.safeParse(run).success && !found.includes(run))
            found.push(run);
    return found;
}
/**
 * The text cut at the card's blanks. A blank the contract would refuse — outside the text, empty,
 * out of order or overlapping the one before — is not drawn, and its characters stay plain text.
 */
export function notePieces(card) {
    const pieces = [];
    let at = 0;
    for (const blank of card.blanks) {
        if (!(blank.start >= at && blank.start < blank.end && blank.end <= card.text.length))
            continue;
        if (blank.start > at)
            pieces.push({ text: card.text.slice(at, blank.start) });
        pieces.push({ text: card.text.slice(blank.start, blank.end), hint: blank.hint });
        at = blank.end;
    }
    if (at < card.text.length)
        pieces.push({ text: card.text.slice(at) });
    return pieces;
}
export function NoteDraftCard({ id, card, writeClipboard = browserClipboard, }) {
    const headId = useId();
    const reasonId = useId();
    const text = card.text;
    const used = noteLength(text);
    const limit = noteLimit(card);
    const over = used > limit;
    const tokens = tokensIn(text);
    const pieces = notePieces(card);
    const blanks = pieces.filter((p) => p.hint !== undefined).length;
    const withheld = over || tokens.length > 0;
    // The status belongs to the text it was for: a redrafted card (the same id, new text) reads 'Copy'.
    const [copy, setCopy] = useState({
        status: "idle",
        text,
    });
    const status = copy.text === text ? copy.status : "idle";
    useAnnounceWhen(status === "copied", t("note.copied"));
    useAnnounceWhen(status === "failed", t("note.copyFailed"), "assertive");
    const timer = useRef(null);
    const live = useRef(true);
    useEffect(() => {
        live.current = true;
        return () => {
            live.current = false;
            if (timer.current !== null)
                clearTimeout(timer.current);
        };
    }, []);
    const onCopy = useCallback(() => {
        if (withheld)
            return;
        const stopTimer = () => {
            if (timer.current !== null)
                clearTimeout(timer.current);
            timer.current = null;
        };
        stopTimer();
        const settle = (next) => {
            if (!live.current)
                return;
            // A second press's copy can settle after the first's: only the latest 'Copied' times out.
            stopTimer();
            setCopy({ status: next, text });
            if (next === "copied")
                timer.current = setTimeout(() => {
                    setCopy({ status: "idle", text });
                }, COPIED_MS);
        };
        let written;
        try {
            written = writeClipboard(text);
        }
        catch {
            settle("failed");
            return;
        }
        written.then(() => {
            settle("copied");
        }, () => {
            settle("failed");
        });
    }, [withheld, writeClipboard, text]);
    const near = !over && used >= limit - NEAR;
    const pct = Math.min(100, (used / limit) * 100);
    return (_jsxs("article", { className: cn("wp-notecard", over && "is-over"), "aria-labelledby": headId, "data-note-draft": id, children: [_jsxs("header", { className: "wp-notecard__head", children: [_jsx("span", { className: "wp-notecard__icon", "aria-hidden": "true", children: _jsx(Icon, { name: "pencil", size: 14 }) }), _jsx("span", { className: "wp-card__over", id: headId, children: t("note.title") }), _jsx("span", { className: "wp-grow" }), _jsxs("span", { className: cn("wp-notecard__count", near && "is-near", over && "is-over"), "data-note-count": used, children: [_jsxs("span", { "aria-hidden": "true", children: [_jsx("b", { children: String(used) }), " / ", String(limit)] }), _jsx("span", { className: "sr-only", children: t("note.counter", { used, max: limit }) })] })] }), _jsx("p", { className: "wp-notecard__text", "data-note-text": "", children: pieces.map((piece, i) => piece.hint === undefined ? (piece.text) : (_jsxs("mark", { className: "wp-blank", title: t("note.blankOf", { hint: piece.hint, blank: t("note.blank") }), "data-blank": "", children: [_jsx(Icon, { name: "pencil", size: 12 }), _jsx("span", { "data-blank-text": "", children: piece.text }), _jsx("span", { className: "sr-only select-none", "data-blank-label": "", children: t("note.blankOf", { hint: piece.hint, blank: t("note.blank") }) })] }, i))) }), _jsx("div", { className: cn("wp-meter", (near || over) && "is-near"), "aria-hidden": "true", children: _jsx("span", { style: { width: `${pct.toFixed(1)}%` } }) }), blanks > 0 ? (_jsxs("p", { className: "wp-notecard__hint", "data-note-blank-line": "", children: [_jsx(Icon, { name: "pencil", size: 12 }), _jsx("span", { children: t("note.blankLegend", { count: blanks }) })] })) : null, withheld ? (_jsxs("div", { id: reasonId, className: "wp-notecard__why", "data-note-withheld": "", children: [over ? (_jsxs("p", { className: "wp-notecard__hint wp-notecard__hint--caution", "data-withheld": "length", children: [_jsx(Icon, { name: "alert", size: 12 }), _jsx("span", { children: t("note.tooLong", { count: used - limit, max: limit }) })] })) : null, tokens.length > 0 ? (_jsxs("p", { className: "wp-notecard__hint wp-notecard__hint--caution", "data-withheld": "token", children: [_jsx(Icon, { name: "alert", size: 12 }), _jsx("span", { children: t("note.hasToken", { count: tokens.length, tokens: tokens.join(", ") }) })] })) : null] })) : null, status === "failed" ? (_jsxs("p", { className: "wp-notecard__hint wp-notecard__hint--caution", "data-note-copy-failed": "", children: [_jsx(Icon, { name: "alert", size: 12 }), _jsx("span", { children: t("note.copyFailed") })] })) : null, _jsxs("footer", { className: "wp-notecard__foot", children: [_jsx("span", { className: "wp-grow" }), _jsx(Button, { compact: true, variant: status === "copied" ? "quiet" : "primary", icon: _jsx(Icon, { name: status === "copied" ? "check" : "copy", size: 14 }), 
                        // Withheld: no hover or press response either, so it does not look pressable.
                        className: cn("wp-notecard__copy", withheld && "hover:brightness-100 active:translate-y-0"), "aria-disabled": withheld ? true : undefined, "aria-describedby": withheld ? reasonId : undefined, onClick: onCopy, "data-note-copy": "", children: status === "copied" ? t("note.copied") : t("note.copy") })] })] }));
}
//# sourceMappingURL=note-draft.js.map